import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Star, DollarSign, Loader2, Camera, X, Image, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewForm({ requestId, providerName, onSubmit, getAuthHeaders }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => 
      ['image/jpeg', 'image/png', 'image/webp'].includes(f.type) && f.size <= 10 * 1024 * 1024
    );

    if (validFiles.length < files.length) {
      toast.error('Some files were skipped (must be JPEG/PNG/WebP, under 10MB)');
    }

    const newPhotos = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      caption: '',
    }));

    setPhotos(prev => [...prev, ...newPhotos].slice(0, 4));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const uploadPhotosToGallery = async () => {
    if (photos.length === 0) return;
    setUploadingPhotos(true);
    const headers = getAuthHeaders();
    let uploaded = 0;

    for (const photo of photos) {
      try {
        const formData = new FormData();
        formData.append('file', photo.file);
        formData.append('request_id', requestId);
        formData.append('caption', photo.caption || `Service by ${providerName}`);

        await axios.post(`${API}/gallery/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        uploaded++;
      } catch (err) {
        // Silently skip failed uploads (e.g. duplicates, customer not provider)
        if (process.env.NODE_ENV === 'development') console.log('Photo upload skipped:', err.response?.data?.detail || err.message);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} submitted for gallery review!`);
    }
    setUploadingPhotos(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/reviews/service/${requestId}`,
        {
          rating,
          comment,
          tip_amount: parseFloat(tipAmount) || 0
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      // Upload photos to gallery after review
      if (photos.length > 0) {
        await uploadPhotosToGallery();
      }

      toast.success('Review submitted! Thank you for your feedback.');
      setSubmitted(true);
      if (onSubmit) onSubmit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="py-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Star className="w-6 h-6 text-green-600 fill-green-600" />
          </div>
          <h3 className="font-semibold text-green-800">Thank You!</h3>
          <p className="text-green-600 text-sm">Your review has been submitted.</p>
          {photos.length > 0 && (
            <p className="text-xs text-green-500 mt-1">Your photos are being reviewed for our gallery.</p>
          )}
          <p className="text-xs text-green-500 mt-2">You can edit your review within 4 hours.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="review-form">
      <CardHeader>
        <CardTitle className="text-lg">Rate Your Experience</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How was your service with {providerName}?
            </label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="p-1 hover:scale-110 transition-transform"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= rating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent!'}
            </p>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share your experience (optional)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell others about your experience..."
              rows={3}
              data-testid="review-comment"
            />
          </div>

          {/* Photo Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share photos of the completed work (optional)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Help other customers see real results! Photos are AI-reviewed for privacy before appearing in our gallery.
            </p>
            
            {/* Photo Previews */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
                    <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${idx}`}
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {photos.length < 4 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#FF3008] hover:text-[#FF3008] transition-colors flex items-center justify-center gap-2"
                data-testid="add-photos-btn"
              >
                <Camera className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {photos.length === 0 ? 'Add Before/After Photos' : `Add More (${4 - photos.length} remaining)`}
                </span>
              </button>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handlePhotoSelect}
              className="hidden"
              data-testid="photo-file-input"
            />
          </div>

          {/* Tip */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add a tip for {providerName}? (optional)
            </label>
            <div className="flex gap-2">
              {[5, 10, 15, 20].map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => setTipAmount(amount.toString())}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    tipAmount === amount.toString()
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`tip-${amount}`}
                >
                  ${amount}
                </button>
              ))}
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  placeholder="Custom"
                  className="pl-8"
                  data-testid="tip-amount"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              100% of tips go directly to your service provider
            </p>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#FF3008] hover:bg-[#E02800]"
            disabled={loading || uploadingPhotos}
            data-testid="submit-review"
          >
            {loading || uploadingPhotos ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              `Submit Review${tipAmount ? ` & $${tipAmount} Tip` : ''}${photos.length > 0 ? ` + ${photos.length} Photo${photos.length > 1 ? 's' : ''}` : ''}`
            )}
          </Button>
          
          <p className="text-xs text-gray-400 text-center">
            You can edit your review within 4 hours of submission
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
